import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { radiusHalf, radiusDouble } from 'utility/borderRadius';

// Dropzone Styles
export const importDropzoneSx = (
  theme: Theme,
  isDragActive: boolean,
): SystemStyleObject => ({
  height: 125,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 1.5,
  color: theme.palette.text.secondary,
  backgroundColor: isDragActive
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  borderRadius: radiusHalf(theme),
  border: `2px dashed ${
    isDragActive ? theme.palette.action.selected : theme.palette.primary.main
  }`,
  '&:hover': {
    border: `2px dashed ${theme.palette.action.selected}`,
    backgroundColor: theme.palette.action.selected,
    opacity: 0.8,
  },
  opacity: isDragActive ? 0.8 : 1,
});

export const importHeaderSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  gap: 1.5,
  // position: 'sticky',
  top: 0,
  zIndex: 1,
  pb: 2,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.blox.colors.offblack
      : theme.blox.colors.offwhite,
  backgroundImage:
    'linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08))',
});

// Status Icon Styles
export const importStatusIconSx = (theme: Theme): SystemStyleObject => ({
  position: 'absolute',
  color: theme.palette.primary[theme.palette.mode === 'light' ? 'light' : 'dark'],
});

// Error Icon Styles
export const importButtonStatusIconSx = (
  theme: Theme,
  border: boolean,
  backgroundColor?: string,
  color?: string,
): SystemStyleObject => ({
  position: 'absolute',
  bottom: 4,
  right: 4,
  width: 16,
  height: 16,
  color: color,
  backgroundColor:
    backgroundColor ||
    theme.palette.common[theme.palette.mode === 'light' ? 'white' : 'black'],
  borderRadius: '50%',
  border: border
    ? `1px solid ${
        theme.palette.common[theme.palette.mode === 'light' ? 'white' : 'black']
      }`
    : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  lineHeight: 1,
  padding: 0,
});

// Progress Bar Styles
export const importProgressSx = (theme: Theme): SystemStyleObject => ({
  borderRadius: 5,
  '&.MuiLinearProgress-colorPrimary': {
    backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
  },
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
    backgroundColor:
      theme.palette.primary[theme.palette.mode === 'light' ? 'light' : 'main'],
  },
});

// Files List Styles
export const importFilesSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  gap: 1.5,
  overflowY: 'auto',
  pr: '6px', // to prevent content from being hidden behind the scrollbar,
  '& .MuiTypography-root, .MuiButtonBase-root': {
    fontSize: 16,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
});

export const importSingleSx = (theme: Theme, isNested = false): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  padding: 1,
  minHeight: '40px',
  borderRadius: radiusHalf(theme),
  backgroundColor:
    theme.palette.grey[
      theme.palette.mode === 'light' ? (isNested ? 200 : 100) : isNested ? 900 : 800
    ],
  opacity: 1,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    opacity: 0.8,
  },
});

export const importDataSx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  alignItems: {
    xs: 'flex-start',
    md: 'center',
  },
  gap: 1.5,
  color: theme.palette.text.secondary,
});

export const importGroupContainerSx = (theme: Theme): SystemStyleObject => ({
  display: 'grid',
  borderRadius: '8px',
  backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 100 : 800],
  backgroundImage: 'unset',
  alignContent: 'center',
  p: 0,
  '&:first-of-type, &:last-of-type': {
    borderTopLeftRadius: radiusDouble(theme),
    borderTopRightRadius: radiusDouble(theme),
    borderBottomLeftRadius: radiusDouble(theme),
    borderBottomRightRadius: radiusDouble(theme),
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    opacity: 0.8,
  },
  '&:before': {
    display: 'none',
  },
  boxShadow: 'unset',
  '& .MuiAccordionSummary-root': {
    borderBottom: 0,
    p: 1,
    mb: 0,
  },
});

export const importGroupHeaderSx = (theme: Theme): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  height: '40px',
  pb: 1,
  mb: 0.5,
  minHeight: 'unset !important',
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  '& .MuiAccordionSummary-content': {
    m: 0,
  },
});
